// client/src/components/pages/Settings/SettingsHome.tsx (converted from .jsx, ST-00222)
//
// The settings hub. A card is shown only to users who may open its page: the page
// permission (config/pagePermissions) and, for the global-admin-only pages (Tenant
// Management, Database Performance), the app role in `requiredAppRoles` — an
// organisation administrator holds every permission key ('*') but those pages'
// APIs answer only global admins (audit IAM-17, IAM-19).
import { useState, useEffect, useRef, type ReactNode } from 'react';
import { useNavigate } from 'react-router-dom';
import { Search, X, ChevronRight, Eye, Settings, type LucideIcon } from 'lucide-react';
import {
  searchSettings,
  type SettingsSearchItem,
  type SettingsSection,
} from '../../../config/settingsRegistry';
import { useDemoMode } from '../../../context/DemoModeContext';
import { useAuth } from '../../../context/AuthContext';
import { getRequiredPermission, permits } from '../../../config/pagePermissions';
import { PageHeader } from '../../../types/commonComponents';
import './SettingsHome.css';

/** The cards of one category, as the hub groups the (filtered) search results. */
interface SettingsGroup {
  id: string;
  label: string;
  icon: LucideIcon;
  section: SettingsSection;
  items: SettingsSearchItem[];
}

const escapeRegExp = (text: string): string => text.replace(/[.*+?^${}()|[\]\\]/g, '\\$&');

/** The label with each occurrence of the search query marked. */
const highlightText = (text: string, query: string): ReactNode => {
  if (!query) return text;
  // Escaped: a query like "(" used to throw an invalid-RegExp error and blank the hub.
  const parts = text.split(new RegExp(`(${escapeRegExp(query)})`, 'gi'));
  return (
    <>
      {parts.map((part, i) =>
        part.toLowerCase() === query.toLowerCase() ? (
          <mark key={i} className="settings-highlight">
            {part}
          </mark>
        ) : (
          <span key={i}>{part}</span>
        )
      )}
    </>
  );
};

interface SettingsCardProps {
  category: SettingsGroup;
  searchQuery: string;
  onItemClick: (item: SettingsSearchItem) => void;
}

const SettingsCard = ({ category, searchQuery, onItemClick }: SettingsCardProps) => {
  const Icon = category.icon;

  return (
    <div className="settings-card">
      <div className="settings-card-header">
        <Icon className="settings-card-icon" size={20} />
        <h3 className="settings-card-title">{category.label}</h3>
      </div>
      <div className="settings-card-items">
        {category.items.map((item) => (
          <button
            key={item.id}
            className={`settings-item ${!item.implemented ? 'settings-item-disabled' : ''}`}
            onClick={() => onItemClick(item)}
            disabled={!item.implemented}
            // The description on hover tells look-alike items apart, e.g. Notifications →
            // Notification Channels (you, this device) vs LexiStar → Notifications (rules).
            title={item.description}
          >
            <div className="settings-item-content">
              <span className="settings-item-label">
                {searchQuery ? highlightText(item.label, searchQuery) : item.label}
              </span>
              {!item.implemented && <span className="settings-item-badge">Coming Soon</span>}
            </div>
            {item.implemented && <ChevronRight size={14} className="settings-item-arrow" />}
          </button>
        ))}
      </div>
    </div>
  );
};

const SettingsHome = () => {
  const [searchQuery, setSearchQuery] = useState('');
  const [filteredResults, setFilteredResults] = useState<SettingsSearchItem[]>([]);
  const searchInputRef = useRef<HTMLInputElement | null>(null);
  const navigate = useNavigate();
  const { canAccessDemoMode, isDemoMode } = useDemoMode();
  const { hasPermission, hasAnyAppRole } = useAuth();

  // Hide settings entries the user cannot open: the destination route is
  // `/settings${item.route}` (its permission resolved centrally), and a
  // global-admin-only page also needs that app role.
  const canSeeItem = (item: SettingsSearchItem): boolean => {
    if (item.requiredAppRoles && !hasAnyAppRole(item.requiredAppRoles)) return false;
    const permission = getRequiredPermission(`/settings${item.route || ''}`);
    return permits(permission, hasPermission);
  };

  const organizationName = 'LexiBridge Operations';

  useEffect(() => {
    setFilteredResults(searchSettings(searchQuery));
  }, [searchQuery]);

  useEffect(() => {
    const handleKeyDown = (e: KeyboardEvent) => {
      if (e.key === '/' && !e.ctrlKey && !e.metaKey && !e.altKey) {
        if (document.activeElement?.tagName !== 'INPUT') {
          e.preventDefault();
          searchInputRef.current?.focus();
        }
      }
      if (e.key === 'Escape' && searchInputRef.current === document.activeElement) {
        setSearchQuery('');
        searchInputRef.current?.blur();
      }
    };

    document.addEventListener('keydown', handleKeyDown);
    return () => document.removeEventListener('keydown', handleKeyDown);
  }, []);

  const handleItemClick = (item: SettingsSearchItem) => {
    if (item.implemented) {
      navigate(`/settings${item.route}`);
    }
  };

  const groupedResults = filteredResults
    .filter(canSeeItem)
    .reduce<Record<string, SettingsGroup>>((acc, item) => {
      const categoryKey = `${item.categoryId}-${item.section}`;
      const group = (acc[categoryKey] ??= {
        id: item.categoryId,
        label: item.categoryLabel,
        icon: item.categoryIcon,
        section: item.section,
        items: [],
      });
      group.items.push(item);
      return acc;
    }, {});

  const organizationCategories = Object.values(groupedResults).filter(
    (cat) => cat.section === 'organization'
  );
  const moduleCategories = Object.values(groupedResults).filter((cat) => cat.section === 'module');

  return (
    <div className="min-h-screen bg-slate-50">
      <PageHeader
        icon={Settings}
        title="Settings"
        subtitle={organizationName}
        toolbar={
          <div className="settings-search-wrapper">
            <Search className="settings-search-icon" size={16} />
            <input
              ref={searchInputRef}
              type="text"
              className="settings-search-input"
              placeholder="Search...  ( / )"
              value={searchQuery}
              onChange={(e) => setSearchQuery(e.target.value)}
            />
            {searchQuery && (
              <button
                className="settings-search-clear"
                onClick={() => setSearchQuery('')}
                aria-label="Clear search"
              >
                <X size={14} />
              </button>
            )}
          </div>
        }
      />

      {/* Main Content */}
      <div className="p-3 sm:p-6 max-w-[1800px] mx-auto">
        <div className="settings-content">
          {organizationCategories.length > 0 && (
            <section>
              <h2 className="settings-section-title">Organization</h2>
              <div className="settings-grid">
                {organizationCategories.map((category) => (
                  <SettingsCard
                    key={category.id}
                    category={category}
                    searchQuery={searchQuery}
                    onItemClick={handleItemClick}
                  />
                ))}
              </div>
            </section>
          )}

          {moduleCategories.length > 0 && (
            <section>
              <h2 className="settings-section-title">Modules</h2>
              <div className="settings-grid">
                {moduleCategories.map((category) => (
                  <SettingsCard
                    key={category.id}
                    category={category}
                    searchQuery={searchQuery}
                    onItemClick={handleItemClick}
                  />
                ))}
              </div>
            </section>
          )}

          {canAccessDemoMode && !searchQuery && (
            <section>
              <h2 className="settings-section-title">Presentation</h2>
              <div className="settings-grid">
                <div className="settings-card">
                  <div className="settings-card-header">
                    <Eye className="settings-card-icon" size={20} />
                    <h3 className="settings-card-title">Demo Mode</h3>
                  </div>
                  <div className="settings-card-items">
                    <button
                      className="settings-item"
                      onClick={() => navigate('/settings/presentation/demo-mode')}
                    >
                      <div className="settings-item-content">
                        <span className="settings-item-label">Data Masking</span>
                        {isDemoMode && (
                          <span className="settings-item-badge settings-item-badge-active">
                            Active
                          </span>
                        )}
                      </div>
                      <ChevronRight size={14} className="settings-item-arrow" />
                    </button>
                  </div>
                </div>
              </div>
            </section>
          )}

          {filteredResults.length === 0 && searchQuery && (
            <div className="settings-no-results">
              <Search size={40} className="settings-no-results-icon" />
              <h3>No settings found</h3>
              <p>Try a different search term</p>
            </div>
          )}
        </div>
      </div>
    </div>
  );
};

export default SettingsHome;
